import type { ReactElement } from 'react';

type IconProps = { size?: number; title?: string };

const svgProps = (
  size: number | undefined,
  title: string | undefined,
): Record<string, unknown> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': title === undefined ? true : undefined,
  role: title === undefined ? 'presentation' : 'img',
});

export function SearchIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <circle cx="10.5" cy="10.5" r="7" />
      <path d="m16 16 5.5 5.5" />
    </svg>
  );
}

export function PlayIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  );
}

export function MenuIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

export function CloseIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="M5 5l14 14M19 5 5 19" />
    </svg>
  );
}

export function ChevronRightIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}

export function ChevronDownIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="m5 9 7 7 7-7" />
    </svg>
  );
}

export function ExternalIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)} strokeWidth={1.6}>
      {title !== undefined && <title>{title}</title>}
      <path d="M8 6h10v10" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

export function InstagramIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
      <circle cx="12" cy="12" r="3.8" />
      <circle cx="17.2" cy="6.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function BlueskyIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)} fill="currentColor" stroke="none">
      {title !== undefined && <title>{title}</title>}
      <path d="M12 10.8c-.9-1.8-3.4-5.2-5.7-6.8C4.1 2.4 3.2 2.7 2.6 3c-.7.3-.8 1.4-.8 2 0 .7.4 5.5.6 6.3.8 2.6 3.7 3.5 6.3 3.2-3.9.6-7.3 2-2.8 7 5 5.2 6.8-1.1 7.8-4.3 1 3.2 2.1 9.3 7.7 4.3 4.2-4.3 1.1-6.4-2.7-7 2.6.3 5.5-.6 6.3-3.2.2-.8.6-5.6.6-6.3 0-.6-.1-1.7-.8-2-.6-.3-1.5-.6-3.7 1-2.3 1.6-4.8 5-5.7 6.8Z" />
    </svg>
  );
}

export function LinkedInIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)} fill="currentColor" stroke="none">
      {title !== undefined && <title>{title}</title>}
      <path d="M4.5 9.5h3.4V20H4.5zM6.2 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM9.9 9.5h3.3V11c.5-.9 1.6-1.8 3.3-1.8 3.5 0 4.1 2.3 4.1 5.2V20h-3.4v-4.9c0-1.2 0-2.7-1.6-2.7s-1.9 1.3-1.9 2.6v5H9.9z" />
    </svg>
  );
}

export function YouTubeIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)} fill="currentColor" stroke="none">
      {title !== undefined && <title>{title}</title>}
      <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8ZM10 15V9l5.2 3Z" />
    </svg>
  );
}

export function FacebookIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)} fill="currentColor" stroke="none">
      {title !== undefined && <title>{title}</title>}
      <path d="M13.5 21v-7.6h2.6l.4-3h-3V8.5c0-.9.3-1.5 1.5-1.5h1.6V4.3A21 21 0 0 0 14.3 4c-2.3 0-3.9 1.4-3.9 4v2.4H7.8v3h2.6V21Z" />
    </svg>
  );
}

export function PhoneIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    </svg>
  );
}

export function PersonIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

export function ArrowRightIcon({ size, title }: IconProps): ReactElement {
  return (
    <svg {...svgProps(size, title)}>
      {title !== undefined && <title>{title}</title>}
      <path d="M4 12h16m-6-6 6 6-6 6" />
    </svg>
  );
}
